import React, { useState } from "react";
import cn from "classnames";

import { useAppSelector } from "@litres-core/store";
import { useTranslation } from "@litres-libs/i18n";
import { BottomSheet } from "@litres/ui-kit/bottom-sheet";
import { SpriteIcon } from "@litres-libs/sprite-icons";
import { Popover } from "@litres/ui-kit/popover";

import { FunctionalButton } from "@litres/ui-kit/functional-button";
import { logErrorToSentry } from "@litres/utils";
import styles from "./AdvertisingMarking.module.scss";

interface IAdvertisingMarkingProps {
  marking: string;
  link?: string | null;
  hasIcon?: boolean;
  forBanner?: boolean;
}

export const AdvertisingMarking = ({
  marking,
  link,
  hasIcon,
  forBanner,
}: IAdvertisingMarkingProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const isPDA = useAppSelector(state => state.browser.pda);
  const handleVisible = (open: boolean) => {
    setIsOpen(open);
  };

  const handleOpen = (e: React.MouseEvent<HTMLDivElement>) => {
    /*
     * При клике на враппер арта на планшетах и мобилках должен происходить редирект на карточку книги
     * блокируем всплытие, т.к. клик на эту кнопку не должен вызывать редирект
     * */
    e.stopPropagation();

    setIsOpen(true);
  };

  const handleClose = (e?: React.MouseEvent<HTMLElement>) => {
    /*
     * При клике на враппер арта на планшетах и мобилках должен происходить редирект на карточку книги
     * блокируем всплытие, т.к. клик на эту кнопку не должен вызывать редирект
     * */
    e?.stopPropagation();

    setIsOpen(false);
  };

  const handleLinkClick = (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    event.stopPropagation();
    if (link) {
      if (forBanner) {
        window.navigator.clipboard.writeText(link);
      } else {
        const origin = window?.location.origin;
        window.navigator.clipboard.writeText(origin + link);
      }
    } else {
      logErrorToSentry(
        "No link provided for copying in AdvertisingMarking component"
      );
    }
  };

  const Content = () => (
    <>
      <div className={styles.content}>
        <p className={styles.content__text}>{marking}</p>
        {link && (
          <FunctionalButton
            className={styles.copyLink}
            onClick={handleLinkClick}
          >
            Скопировать ссылку
          </FunctionalButton>
        )}
      </div>
      {!isPDA && (
        <SpriteIcon
          name="close"
          className={cn(styles.icon, styles.icon__close)}
          onClick={handleClose}
          size={16}
        />
      )}
    </>
  );

  const Title = () => (
    <div className={styles.container} onClick={handleOpen} tabIndex={0}>
      <p className={styles.title}>{t("Реклама")}</p>
      {hasIcon && (
        <SpriteIcon name="infoFilled" size={16} className={styles.icon} />
      )}
    </div>
  );

  return isPDA ? (
    <>
      <Title />
      <BottomSheet open={isOpen} onClose={handleClose}>
        <Content />
      </BottomSheet>
    </>
  ) : (
    <Popover
      content={<Content />}
      contentClassName={styles.tooltip}
      baseClassName={styles.tooltip__anchor}
      visible={isOpen}
      onVisibleChange={handleVisible}
      customCloseTriggers={["lostIntersect"]}
      trigger="click"
      placement="bottom"
      strategy="absolute"
      offset={[10, -52]}
      disableArrow
    >
      <div className={styles.container} tabIndex={0}>
        <p className={styles.title}>{t("Реклама")}</p>
        {hasIcon && (
          <SpriteIcon name="infoFilled" size={16} className={styles.icon} />
        )}
      </div>
    </Popover>
  );
};
